import React, { useState, useEffect, useRef } from 'react';
import api from './api/axiosInstance';
import Sidebar from './Sidebar.js';
import { Card, Row, Col, Container, Button, Modal } from 'react-bootstrap';
import { exportCityPdf } from './api/exportCityPdf.js';
import { useParams, useNavigate } from 'react-router-dom';
import CityInsights from './CityInsights';
import { TicketCountContext } from './TicketCountContext';
import Breadcrumb from './Breadcrumb.js';
import CityGeneralInfo from './CityGeneralInfo.js';
import NavTabs from './NavTabs';

const CityEditInsights = () => {
    const { id } = useParams();
    const insightsRef = useRef(null);
    const [errors, setErrors] = useState({}); // State for validation errors
    const [loading, setLoading] = useState(true); // ✅ Show spinner while fetching
    const [showDeleteModal, setShowDeleteModal] = useState(false); // Delete modal state
    const navigate = useNavigate();
    const { ticketCount } = React.useContext(TicketCountContext);

    const cityInsightDefaults = {
        service: '',
        name: '',
        url: '',
        providerUrl: '',
        description: '',
        googleRating: '',
        showStatus: false,
    };

    const [formData, setFormData] = useState({
        name: '',
        country: {},
        url: '',
    });

    useEffect(() => {
        let isMounted = true;

        const fetchCity = async () => {
            try {
                const res = await api.get(
                    `/api/cities/${id}`,
                );
                const data = res?.data || {};

                if (!isMounted) return;

                setFormData({
                    name: data?.name || '',
                    country: (data?.country && typeof data.country === 'object') ? data.country : {},
                    url: data?.url || '',
                    cityInsight: {
                        ...cityInsightDefaults,
                        ...(typeof data?.cityInsight === 'object' && data.cityInsight ? data.cityInsight : {}),
                    },
                });
            } catch (err) {
                console.error('Error fetching city:', err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchCity();
        return () => { isMounted = false; };
    }, [id]);

    const handleChange = (e) => {
        const { name, type } = e.target || {};
        if (!name) return;

        const val = type === 'checkbox' ? !!e.target.checked : e.target.value;

        // 1) Nested cityInsight fields via dot path, e.g. "cityInsight.name"
        if (name.startsWith('cityInsight.')) {
            const key = name.slice('cityInsight.'.length);
            setFormData((prev) => ({
            ...prev,
            cityInsight: {
                ...cityInsightDefaults,
                ...(prev.cityInsight || {}),
                [key]: val,
            },
            }));
            return;
        }

        // 2) Replace/merge the entire cityInsight object
        if (name === 'cityInsight' && val && typeof val === 'object') {
            setFormData((prev) => ({
            ...prev,
            cityInsight: {
                ...cityInsightDefaults,
                ...(prev.cityInsight || {}),
                ...val,
            },
            }));
            return;
        }

        // 3) Fallback for any other simple top-level fields
        setFormData((prev) => ({ ...prev, [name]: val }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Build one insight object from current formData.cityInsight
        const insight = {
            ...cityInsightDefaults,
            ...(formData?.cityInsight || {}),
        };

        // Send as multipart/form-data (multer-friendly)
        const fd = new FormData();
        // easiest: send single JSON field "insight"
        fd.append('insight', JSON.stringify(insight));

        try {
            const res = await api.post(
                `/api/cities/${id}/insights`,
                fd,
            );

            // Optional: close modal + reset inline form values
            // and/or refresh city data from response
            // setFormData(prev => ({ ...prev, cityInsights: res.data.cityInsights, cityInsight: cityInsightDefaults }));
        } catch (error) {
            console.error('Error adding insight:', error);
        }
    };


    const handleExportPdf = async () => {
        try {
            await exportCityPdf(id);
        } catch (error) {
            console.error('Error exporting city PDF:', error);
            alert('An error occurred while exporting the city PDF.');
        }
    };

    const handleDeleteCity = () => {
        setShowDeleteModal(true);
    };

    const confirmDeleteCity = async () => {
        try {
            await api.delete(`/api/cities/${id}`);
            setShowDeleteModal(false);
            navigate('/onboarding-city-list'); // Redirect to company list after deletion
        } catch (error) {
            console.error('Error deleting city:', error);
            alert('An error occurred while deleting the city.');
        }
    };

    const userNavItems = [
        { to: '/city-edit/:id', eventKey: 'cityInfo', label: 'About' },
        { to: '/city-edit-companies/:id', eventKey: 'companies', label: 'Companies & Jobs', alsoActive: [`/city-edit-jobs/${id}`] },
        { to: '/city-edit-salary/:id', eventKey: 'salariesInfo', label: 'Average salaries' },
        { to: '/city-edit-insights/:id', eventKey: 'cityInsights', label: 'City insights' },
        { to: '/city-edit-universities/:id', eventKey: 'universities', label: 'Universities' },
        { to: '#', eventKey: 'cityComments', label: 'Comments' },
        { to: '/city-edit-history/:id', eventKey: 'history', label: 'History of changes' },
    ];

    return (
        <>
            <Sidebar ticketCount={ticketCount} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb cityName={formData.name} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <>
                                                <Row>
                                                    <Col>
                                                        <CityGeneralInfo
                                                            formData={formData}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row className="mb-5">
                                                    <Col>
                                                        <NavTabs items={userNavItems} />
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        {loading && 
                                                            <div>Loading...</div>
                                                        }
                                                        {!loading && 
                                                            <CityInsights
                                                                formData={formData}
                                                                handleChange={handleChange}
                                                                errors={errors}
                                                                isEditPage={true}
                                                                handleSubmit={handleSubmit}
                                                                setFormData={setFormData}
                                                                ref={insightsRef}
                                                            />
                                                        }
                                                    </Col>
                                                </Row>
                                            </>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col>
                            <Button variant="outline-secondary" className="ms-3" onClick={() => navigate('/onboarding-city-list')}>
                                Cancel
                            </Button>
                            <Button variant="outline-danger" onClick={handleDeleteCity}>
                                Delete City
                            </Button>
                            <Button variant="outline-secondary" onClick={handleExportPdf}>
                                Download city report
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </main>
        </>
    );
};
export default CityEditInsights;
